import React, { useEffect, useState } from 'react'
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Select,
  Upload,
  message,
  Divider,
  Space,
  DatePicker,
  InputNumber,
  Table,
} from 'antd'
import { UploadOutlined, SaveOutlined, EditOutlined } from '@ant-design/icons'
import AdminHeader from '../CommonComponents/AdminHeader'
import AdminFooter from '../CommonComponents/AdminFooter'
import './StudentAdmissionForm.css'
import { toast } from 'react-toastify'
import {
  getCaseFileData,
  getCaseFileDataById,
  getChildrenList,
  IMG_URL,
  saveCaseFileData,
  updateCaseFileData,
} from '../APIComponents/AdminApi'
import dayjs from 'dayjs'

const { Option } = Select
const { TextArea } = Input

const CaseFileForm = () => {
  //===============================================
  const loginType = sessionStorage.getItem('loginType') // 'ADMIN' or 'USER'
  const menuData = JSON.parse(sessionStorage.getItem('menus')) || []

  const getPagePermission = () => {
    if (loginType === 'ADMIN') {
      return {
        is_new: 1,
        is_edit: 1,
        is_view: 1,
        is_delete: 1,
      }
    }

    for (const menu of menuData) {
      const page = menu.pages?.find((p) => p.page_name === 'Case File')
      if (page) return page
    }

    return {
      is_new: 0,
      is_edit: 0,
      is_view: 0,
      is_delete: 0,
    }
  }

  const pagePermission = getPagePermission()
  const showActionColumn =
    loginType === 'ADMIN' ||
    pagePermission.is_edit == 1 ||
    pagePermission.is_view == 1 ||
    pagePermission.is_delete == 1
  //============================================

  const [form] = Form.useForm()

  const [students, setStudents] = useState([])
  const [editingCaseId, setEditingCaseId] = useState(null)

  const [showForm, setShowForm] = useState(false)
  const [selectedChildId, setSelectedChildId] = useState(null)

  const [tableData, setTableData] = useState([])
  const [searchText, setSearchText] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [tableLoading, setTableLoading] = useState(false)
  const [studentLoading, setStudentLoading] = useState(false)

  useEffect(() => {
    fetchTableData()
  }, [])

  const fetchTableData = async () => {
    try {
      setTableLoading(true)

      const res = await fetch(getCaseFileData)
      const data = await res.json()
      setTableData(data)
    } catch (err) {
      console.error(err)
    } finally {
      setTableLoading(false)
    }
  }

  useEffect(() => {
    fetch(getChildrenList)
      .then((res) => res.json())
      .then((data) => setStudents(data))
  }, [])

  const handleFinish = async (values) => {
    try {
      const formData = new FormData()
      // formData.append('reg_id', values.childName)
      if (!editingCaseId) {
        formData.append('reg_id', values.childName)
      }
      formData.append('case_number', values.caseNumber)
      formData.append(
        'case_registration_date',
        values.caseDate.format('YYYY-MM-DD'),
      )
      formData.append('case_type', values.caseType)
      formData.append('referred_by', values.referredBy || '')
      formData.append('handled_by', values.handledBy || '')
      formData.append('police_station_name', values.authorityName || '')
      formData.append('description', values.caseSummary)

      // File appending logic remains the same
      const appendFile = async (fieldName, fileList) => {
        if (!fileList || fileList.length === 0) return
        const file = fileList[0]
        if (file.originFileObj) {
          formData.append(fieldName, file.originFileObj)
        } else if (file.url && !file.originFileObj) {
          const response = await fetch(file.url)
          const blob = await response.blob()
          formData.append(fieldName, blob, file.name)
        }
      }

      await appendFile('fir_report', values.policeReport)
      await appendFile('cwc_order', values.cwcOrder)
      await appendFile('statement_copy', values.statementCopy)
      await appendFile('legal_notice', values.legalDocuments)
      await appendFile('social_investigation_report', values.sirReport)
      await appendFile('other_supporting_documents', values.supportingDocuments)

      // Decide API endpoint
      const apiUrl = editingCaseId
        ? `${updateCaseFileData}/${editingCaseId}` // Update API
        : saveCaseFileData // Create API

      const method = editingCaseId ? 'PUT' : 'POST' // PUT for update, POST for create

      const res = await fetch(apiUrl, {
        method,
        body: formData,
      })

      // const data = res.data
      const data = await res.text()

      if (data === 'SAVED') {
        toast.success('Case file submitted successfully!')
        form.resetFields()
        setEditingCaseId(null)
      } else if (data === 'UPDATED') {
        toast.success('Case file updated successfully!')
        form.resetFields()
        setEditingCaseId(null)
      } else {
        toast.error('Something went wrong!')
      }
    } catch (error) {
      console.error(error)
      toast.error('Something went wrong!')
    }
  }

  const handleStudentChange = async (id) => {
    try {
      setStudentLoading(true)
      const res = await fetch(`${getCaseFileDataById}/${id}`)
      const data = await res.json()

      if (data && data.case_number) {
        setEditingCaseId(data.reg_id) // set case id for update
      } else {
        setEditingCaseId(null) // new entry
      }

      const createFileList = (fileName) => {
        if (!fileName) return []

        return [
          {
            uid: fileName,
            name: fileName,
            status: 'done',
            url: `${IMG_URL}${fileName}`,
          },
        ]
      }

      form.setFieldsValue({
        childName: data.reg_id,
        caseNumber: data.case_number,
        caseDate: data.case_registration_date
          ? dayjs(data.case_registration_date)
          : null,
        caseType: data.case_type,
        referredBy: data.referred_by,
        handledBy: data.handled_by,
        authorityName: data.police_station_name,
        caseSummary: data.description,
        policeReport: createFileList(data.fir_report),
        cwcOrder: createFileList(data.cwc_order),
        statementCopy: createFileList(data.statement_copy),
        legalDocuments: createFileList(data.legal_notice),
        sirReport: createFileList(data.social_investigation_report),
        supportingDocuments: createFileList(data.other_supporting_documents),
      })
    } catch (error) {
      console.error(error)
    } finally {
      setStudentLoading(false)
    }
  }

  // ================= EDIT =================
  const handleEditClick = async (id) => {
    try {
      setShowForm(true)
      setEditingCaseId(id)

      const res = await fetch(`${getCaseFileDataById}/${id}`)
      const data = await res.json()

      const createFileList = (fileName) => {
        if (!fileName) return []
        return [
          {
            uid: fileName,
            name: fileName,
            status: 'done',
            url: `${IMG_URL}${fileName}`,
          },
        ]
      }

      setTimeout(() => {
        form.setFieldsValue({
          childName: data.reg_id,
          caseNumber: data.case_number,
          caseDate: data.case_registration_date
            ? dayjs(data.case_registration_date)
            : null,
          caseType: data.case_type,
          referredBy: data.referred_by,
          handledBy: data.handled_by,
          authorityName: data.police_station_name,
          caseSummary: data.description,
          policeReport: createFileList(data.fir_report),
          cwcOrder: createFileList(data.cwc_order),
          statementCopy: createFileList(data.statement_copy),
          legalDocuments: createFileList(data.legal_notice),
          sirReport: createFileList(data.social_investigation_report),
          supportingDocuments: createFileList(data.other_supporting_documents),
        })
      }, 100)
    } catch (err) {
      console.error(err)
    }
  }

  const loadCaseData = async (id) => {
    try {
      const res = await fetch(`${getCaseFileDataById}/${id}`)
      const data = await res.json()

      const createFileList = (fileName) => {
        if (!fileName) return []
        return [
          {
            uid: fileName,
            name: fileName,
            status: 'done',
            url: `${IMG_URL}${fileName}`,
          },
        ]
      }

      form.setFieldsValue({
        childName: data.reg_id,
        caseNumber: data.case_number,
        caseDate: data.case_registration_date
          ? dayjs(data.case_registration_date)
          : null,
        caseType: data.case_type,
        referredBy: data.referred_by,
        handledBy: data.handled_by,
        authorityName: data.police_station_name,
        caseSummary: data.description,
        policeReport: createFileList(data.fir_report),
        cwcOrder: createFileList(data.cwc_order),
        statementCopy: createFileList(data.statement_copy),
        legalDocuments: createFileList(data.legal_notice),
        sirReport: createFileList(data.social_investigation_report),
        supportingDocuments: createFileList(data.other_supporting_documents),
      })
    } catch (err) {
      console.error(err)
    }
  }

  // ================= FILE VALIDATION =================
  const validateFile = (file) => {
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/pdf',
    ]

    const isAllowedType = allowedTypes.includes(file.type)

    if (!isAllowedType) {
      toast.error('Only JPG, PNG, and PDF files are allowed!')
      return Upload.LIST_IGNORE
    }

    const isLt3M = file.size / 1024 / 1024 < 3

    if (!isLt3M) {
      toast.warn('File must be smaller than 3MB!')
      return Upload.LIST_IGNORE
    }

    return false // prevent auto upload
  }

  // ================= TABLE COLUMNS =================
  const columns = [
    {
      title: 'Sr No',
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: 'Child Name',
      render: (item) => `${item?.student.full_name || ''}`,
    },
    {
      title: 'Case No',
      dataIndex: 'case_number',
    },
    {
      title: 'Case Reg. Date',
      render: (_, record) =>
        record.case_registration_date
          ? dayjs(record.case_registration_date).format('DD-MM-YYYY')
          : '-',
    },
    {
      title: 'Case Type',
      dataIndex: 'case_type',
      render: (value) => {
        const caseTypeMap = {
          1: 'Missing Child',
          2: 'Abuse / Exploitation',
          3: 'Neglect',
          4: 'Trafficking',
          5: 'Abandonment',
          6: 'Other',
        }

        return caseTypeMap[value] || '-'
      },
    },
    ...(showActionColumn
      ? [
          {
            title: 'Action',
            render: (item) => (
              <>
                {pagePermission.is_edit == 1 && (
                  <Button
                    type="link"
                    title="Edit"
                    icon={<EditOutlined />}
                    onClick={() => handleEditClick(item.reg_id)}
                  ></Button>
                )}
              </>
            ),
          },
        ]
      : []),
  ]

  return (
    <>
      <AdminHeader />
      <div className="student-form-section container-fluid">
        <div className="student-form-wrapper pb-5">
          <div className="row student-form-title">
            <h3 className="section-title1 text-start mb-3">
              Case File Details & Related Documents
            </h3>
          </div>

          {!showForm ? (
            <>
              <Row justify="space-between" className="mb-3">
                <Col xs={24} md={10} lg={14}></Col>
                <Col
                  xs={24}
                  md={6}
                  lg={4}
                  className="text-end px-md-2 pb-2 pb-md-0"
                >
                  {pagePermission.is_new == 1 && (
                    <Button
                      type="primary"
                      onClick={() => {
                        setShowForm(true)
                        setEditingCaseId(null)
                        form.resetFields()
                      }}
                    >
                      Add Case File
                    </Button>
                  )}
                </Col>

                <Col xs={24} md={6} lg={6}>
                  <Input
                    placeholder="Search by name"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </Col>
              </Row>

              <Table
                dataSource={(tableData || []).filter((item) => {
                  const search = searchText.toLowerCase()

                  return (
                    item?.student?.full_name?.toLowerCase()?.includes(search) ||
                    item?.case_number?.toLowerCase()?.includes(search)
                  )
                })}
                columns={columns}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  showSizeChanger: true,
                  onChange: (page, size) => {
                    setCurrentPage(page)
                    setPageSize(size)
                  },
                }}
                rowKey="id"
                bordered
                loading={tableLoading}
                className="shadow rounded custom-table p-0"
                style={{
                  overflowX: 'auto',
                  border: '1px solid #dfdfdf',
                  backgroundColor: 'white',
                }}
              />
            </>
          ) : (
            <>
              <div className="row student-form-container shadow p-4 rounded bg-white">
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleFinish}
                  autoComplete="off"
                  className="py-4"
                >
                  {/*  Child Selection */}
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Select Children Name"
                        name="childName"
                        rules={[
                          { required: true, message: 'Please select a child' },
                        ]}
                      >
                        <Select
                          loading={studentLoading}
                          disabled={studentLoading}
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option?.children
                              ?.toString()
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          placeholder="Search by name / reg no"
                          onChange={(value) => handleStudentChange(value)}
                        >
                          {students.map((child) => (
                            <Option key={child.id} value={child.id}>
                              {child.fname} {child.mname} {child.lname} (Reg No:{' '}
                              {child.reg_no})
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <div className="divider-left-only">
                    <h5>Case File Details -</h5>
                  </div>

                  {/*  Basic Case Info */}
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Case Number / File No."
                        name="caseNumber"
                        rules={[
                          {
                            required: true,
                            message: 'Please enter case number',
                          },
                        ]}
                      >
                        <Input
                          placeholder="Enter case or file number"
                          onKeyPress={(e) => {
                            if (!/[0-9]/.test(e.key)) {
                              e.preventDefault()
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Date of Case Registration"
                        name="caseDate"
                        rules={[
                          {
                            required: true,
                            message: 'Please select case registration date',
                          },
                        ]}
                      >
                        <DatePicker
                          style={{ width: '100%' }}
                          format="DD-MM-YYYY"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Case Type"
                        name="caseType"
                        rules={[
                          {
                            required: true,
                            message: 'Please select case type',
                          },
                        ]}
                      >
                        <Select placeholder="Select case type">
                          <Option value={1}>Missing Child</Option>
                          <Option value={2}>Abuse / Exploitation</Option>
                          <Option value={3}>Neglect</Option>
                          <Option value={4}>Trafficking</Option>
                          <Option value={5}>Abandonment</Option>
                          <Option value={6}>Other</Option>
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item label="Referred By" name="referredBy">
                        <Input placeholder="Enter who referred the case (Police / CWC / NGO etc.)" />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/*  Legal & Authority Info */}
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Handled By (NGO Staff / Case Worker)"
                        name="handledBy"
                      >
                        <Input placeholder="Enter name of staff handling the case" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        label="CWC / Police Station Name"
                        name="authorityName"
                      >
                        <Input placeholder="Enter concerned authority name" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24}>
                      <Form.Item
                        label="Case Summary / Description"
                        name="caseSummary"
                        rules={[
                          {
                            required: true,
                            message: 'Please provide a case summary',
                          },
                        ]}
                      >
                        <TextArea
                          rows={5}
                          placeholder="Brief summary or details about the case..."
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <div className="divider-left-only">
                    <h5>Case Documents -</h5>
                  </div>

                  {/* 📎 Document Uploads */}
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="FIR / Police Report"
                        name="policeReport"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => e.fileList}
                      >
                        <Upload
                          beforeUpload={validateFile}
                          accept=".jpg,.jpeg,.png,.pdf"
                          listType="picture"
                          maxCount={1}
                          style={{ width: '100%' }}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            style={{ width: '100%' }}
                          >
                            Upload FIR / Report
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        label="CWC Order / Letter"
                        name="cwcOrder"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => e.fileList}
                      >
                        <Upload
                          beforeUpload={validateFile}
                          accept=".jpg,.jpeg,.png,.pdf"
                          listType="picture"
                          maxCount={1}
                          style={{ width: '100%' }}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            style={{ width: '100%' }}
                          >
                            Upload CWC Order
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Statement Copy (if available)"
                        name="statementCopy"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => e.fileList}
                      >
                        <Upload
                          beforeUpload={validateFile}
                          accept=".jpg,.jpeg,.png,.pdf"
                          listType="picture"
                          maxCount={1}
                          style={{ width: '100%' }}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            style={{ width: '100%' }}
                          >
                            Upload Statement Copy
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Court Proceedings / Legal Notices"
                        name="legalDocuments"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => e.fileList}
                      >
                        <Upload
                          beforeUpload={validateFile}
                          accept=".jpg,.jpeg,.png,.pdf"
                          listType="picture"
                          maxCount={1}
                          style={{ width: '100%' }}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            style={{ width: '100%' }}
                          >
                            Upload Legal Docs
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Social Investigation Report (SIR)"
                        name="sirReport"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => e.fileList}
                      >
                        <Upload
                          beforeUpload={validateFile}
                          accept=".jpg,.jpeg,.png,.pdf"
                          listType="picture"
                          maxCount={1}
                          style={{ width: '100%' }}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            style={{ width: '100%' }}
                          >
                            Upload SIR
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Other Supporting Documents"
                        name="supportingDocuments"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => e.fileList}
                      >
                        <Upload
                          beforeUpload={validateFile}
                          accept=".jpg,.jpeg,.png,.pdf"
                          listType="picture"
                          maxCount={1}
                          style={{ width: '100%' }}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            style={{ width: '100%' }}
                          >
                            Upload Other Docs
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider />

                  {/*  Submit Buttons */}
                  <Row justify="end">
                    <Space>
                      <Button
                        htmlType="reset"
                        onClick={() => {
                          setShowForm(false)
                          form.resetFields()
                        }}
                      >
                        Cancel
                      </Button>
                      <Button type="primary" htmlType="submit">
                        Submit
                      </Button>
                    </Space>
                  </Row>
                </Form>
              </div>
            </>
          )}
        </div>
      </div>
      <AdminFooter />
    </>
  )
}

export default CaseFileForm
